import { api } from '@/services/api';
import {
  Invoice,
  PaymentModuleConfig,
  PaymentPlan,
  PaymentTransaction,
  Subscription,
} from '../types';

/**
 * ─── paymentApi ──────────────────────────────────────────────────────────────
 *
 * Seul point de contact HTTP du module de paiement avec le backend de l'app.
 * Le backend ne fait que PERSISTER ces blobs (il ne les interprète pas) :
 * c'est ce qui rend la config admin globale (tous les navigateurs) au lieu
 * d'être locale au localStorage de l'admin.
 *
 * AUCUN appel vers FlexPay / M-Pesa ici — les providers restent la seule
 * couche qui parlera (plus tard, via le backend) aux fournisseurs.
 */

export interface PaymentPublicConfigResponse {
  /** null = jamais configuré côté serveur (le frontend garde ses défauts). */
  config: Partial<PaymentModuleConfig> | null;
  plans: PaymentPlan[] | null;
  freeLimits: {
    dailyPractice: number;
    dailySimulation: number;
  };
}

export const paymentApi = {
  fetchPublicConfig: () =>
    api.get<PaymentPublicConfigResponse>('/public/config/payments'),

  // ── FlexPay mobile money (le backend relaie vers l'API FlexPay) ──
  /** Initie un paiement mobile money : renvoie l'orderNumber (push envoyé). */
  initiateFlexPay: (body: {
    reference: string;
    planId: string;
    phone: string;
    method: string;
  }) =>
    api.post<{ orderNumber: string; reference: string; message: string }>(
      '/payments/flexpay/initiate',
      body,
    ),

  /**
   * Initie un paiement par carte bancaire : renvoie l'URL de la page de
   * paiement hébergée (redirection) + l'orderNumber (doc Card v1.2).
   */
  initiateFlexPayCard: (body: {
    reference: string;
    planId: string;
    method: string;
    returnUrls: { approve: string; cancel: string; decline: string };
    language?: string;
  }) =>
    api.post<{ url: string; orderNumber: string; reference: string }>(
      '/payments/flexpay/card/initiate',
      body,
    ),

  /** Polling du statut d'un paiement FlexPay. */
  flexPayStatus: (orderNumber: string) =>
    api.get<{
      status: 'success' | 'failed' | 'pending' | 'not_found';
      message: string;
      transaction: {
        reference: string;
        orderNumber: string | null;
        amount: number;
        currency: string;
      } | null;
    }>(`/payments/flexpay/status/${encodeURIComponent(orderNumber)}`),

  /** Réservé à l'admin (endpoint gardé côté backend). */
  saveSettings: (data: { config?: PaymentModuleConfig; plans?: PaymentPlan[] }) =>
    api.patch<{ config: unknown; plans: unknown }>('/admin/payments/settings', data),

  // ── Abonnement rattaché au COMPTE (utilisateur connecté, non-invité) ──
  // L'abonnement survit ainsi aux changements d'appareil et à la purge des
  // données locales à la déconnexion.

  getMySubscription: () =>
    api.get<{ subscription: Subscription | null }>('/payments/me/subscription'),

  saveMySubscription: (subscription: Subscription | null) =>
    api.put('/payments/me/subscription', { subscription }),

  /** Envoie la facture par email à l'utilisateur (template backend). */
  sendInvoiceEmail: (invoice: Invoice, paymentMethodLabel?: string | null) =>
    api.post('/payments/me/invoice-email', {
      ...(paymentMethodLabel ? { paymentMethodLabel } : {}),
      id: invoice.id,
      reference: invoice.transactionReference,
      planName: invoice.planName,
      amount: invoice.amount,
      currency: invoice.currency,
      issuedAt: invoice.issuedAt,
      periodStart: invoice.periodStart,
      periodEnd: invoice.periodEnd,
    }),

  // ── Registre SERVEUR des transactions ──
  // Réplication best-effort de l'historique local : c'est ce registre que
  // l'admin consulte pour retrouver un paiement coupé et le régler
  // (re-check API agrégateur ou validation manuelle).

  saveMyTransaction: (tx: PaymentTransaction & { planName?: string }) =>
    api.put('/payments/me/transactions', {
      reference: tx.reference,
      providerOrderNumber: tx.providerOrderNumber,
      planId: tx.planId,
      ...(tx.planName ? { planName: tx.planName } : {}),
      amount: tx.amount,
      currency: tx.currency,
      providerId: tx.providerId,
      method: tx.method,
      status: tx.status,
      failureReason: tx.failureReason,
      completedAt: tx.completedAt,
    }),
};

// ── Endpoints admin (gardés ADMIN côté backend) ──

export interface AdminTransaction {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  reference: string;
  providerOrderNumber: string | null;
  planId: string;
  planName: string;
  amount: number;
  currency: 'USD' | 'CDF';
  providerId: string;
  method: string;
  status: string;
  failureReason: string | null;
  verifiedBy: 'client' | 'api' | 'manual' | null;
  completedAt: string | null;
  invoiceSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPaymentStats {
  daily: {
    date: string;
    count: number;
    success: number;
    revenueUSD: number;
    revenueCDF: number;
  }[];
  totals: {
    total: number;
    success: number;
    failed: number;
    pending: number;
    revenueUSD: number;
    revenueCDF: number;
  };
}

export const adminPaymentsApi = {
  listTransactions: (params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) =>
    api.get<{ items: AdminTransaction[]; total: number }>(
      '/admin/payments/transactions',
      { params },
    ),

  getStats: (days = 30) =>
    api.get<AdminPaymentStats>('/admin/payments/transactions/stats', {
      params: { days },
    }),

  /** Re-check du statut réel auprès de l'API de l'agrégateur. */
  checkTransaction: (id: string) =>
    api.post<{
      outcome: 'success' | 'failed' | 'pending' | 'unavailable';
      message: string;
      transaction: AdminTransaction;
    }>(`/admin/payments/transactions/${id}/check`),

  /** Validation manuelle : abonnement activé + facture envoyée. */
  validateTransaction: (id: string) =>
    api.post<{ message: string; transaction: AdminTransaction }>(
      `/admin/payments/transactions/${id}/validate`,
    ),

  resendInvoice: (id: string) =>
    api.post<{ message: string; transaction: AdminTransaction }>(
      `/admin/payments/transactions/${id}/resend-invoice`,
    ),

  /** Credentials FlexPay (token masqué — jamais renvoyé en clair). */
  getFlexPayConfig: () =>
    api.get<FlexPayConfigResponse>('/admin/payments/flexpay'),

  /** Met à jour les creds FlexPay. Token vide = on conserve l'existant. */
  saveFlexPayConfig: (data: {
    apiUrl?: string;
    merchant?: string;
    token?: string;
    callbackUrlBase?: string;
    cardUrl?: string;
  }) => api.patch<FlexPayConfigResponse>('/admin/payments/flexpay', data),
};

/** Réponse admin des creds FlexPay (token masqué). */
export interface FlexPayConfigResponse {
  apiUrl: string;
  merchant: string;
  callbackUrlBase: string;
  cardUrl: string;
  tokenSet: boolean;
  tokenPreview: string;
  /** Mobile money exploitable (token + marchand + URL d'API). */
  mobileMoneyConfigured: boolean;
  /** Carte bancaire exploitable (token + marchand + URL carte + URL d'API). */
  cardConfigured: boolean;
}
